import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Search, 
  Edit,
  Trash2,
  UserCog,
  Mail,
  Phone,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const EmployeesPage = () => {
  const { t, language } = useLanguage();
  const { canManageEmployees, user: currentUser } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    role: 'assistant',
    is_active: true
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await usersAPI.getAll();
      // Filter out clients - show only admin, lawyers, and assistants
      const staffOnly = response.data.filter(u => u.role !== 'client');
      setEmployees(staffOnly);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.username;
        await usersAPI.update(editingEmployee.id, updateData);
        toast.success(language === 'ar' ? 'تم تحديث الموظف' : 'Employee updated');
      } else {
        await usersAPI.create(formData);
        toast.success(language === 'ar' ? 'تم إضافة الموظف' : 'Employee created');
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error(language === 'ar' ? 'لا يمكنك حذف حسابك' : 'Cannot delete your own account');
      return;
    }
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا الموظف؟' : 'Are you sure you want to delete this employee?')) return;
    try {
      await usersAPI.delete(id);
      // Update state immediately
      setEmployees(prev => prev.filter(emp => emp.id !== id));
      toast.success(language === 'ar' ? 'تم حذف الموظف بنجاح' : 'Employee deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حذف الموظف' : 'Failed to delete employee'));
      // Refresh list in case of error
      fetchEmployees();
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      role: 'assistant',
      is_active: true
    });
    setEditingEmployee(null);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      username: employee.username,
      password: '',
      full_name: employee.full_name,
      email: employee.email,
      phone: employee.phone || '',
      role: employee.role,
      is_active: employee.is_active
    });
    setDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      admin: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', label: t('admin') },
      lawyer: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', label: t('lawyer') },
      assistant: { color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400', label: t('assistant') }
    };
    const r = roleMap[role] || roleMap.assistant;
    return <Badge className={r.color}>{r.label}</Badge>;
  };

  const filteredEmployees = employees.filter(e => 
    e.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="employees-page">
      <Header title={t('employees')} />
      
      <div className="p-6 space-y-6">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 sm:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-employees"
            />
          </div>

          {canManageEmployees && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="create-employee-btn">
                  <Plus className="w-4 h-4" />
                  {t('createEmployee')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingEmployee ? t('editEmployee') : t('createEmployee')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('username')} *</Label>
                      <Input
                        value={formData.username}
                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                        required
                        disabled={!!editingEmployee}
                        data-testid="employee-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{editingEmployee ? `${t('password')} (${language === 'ar' ? 'اختياري' : 'optional'})` : `${t('password')} *`}</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required={!editingEmployee}
                        data-testid="employee-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fullName')} *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('email')} *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        data-testid="employee-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('phone')}</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        data-testid="employee-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('role')} *</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(v) => setFormData({...formData, role: v})}
                    >
                      <SelectTrigger data-testid="employee-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{t('admin')}</SelectItem>
                        <SelectItem value="lawyer">{t('lawyer')}</SelectItem>
                        <SelectItem value="assistant">{t('assistant')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{language === 'ar' ? 'الحساب نشط' : 'Account Active'}</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                      data-testid="employee-active-switch"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="btn-accent" data-testid="employee-submit-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Employees Grid */}
        {filteredEmployees.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEmployees.map((employee) => (
              <Card 
                key={employee.id} 
                className={`hover:shadow-md transition-shadow ${!employee.is_active ? 'opacity-60' : ''}`}
                data-testid={`employee-card-${employee.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserCog className="w-5 h-5 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{employee.full_name}</CardTitle>
                        <p className="text-xs text-muted-foreground">@{employee.username}</p>
                      </div>
                    </div>
                    {getRoleBadge(employee.role)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" strokeWidth={1.5} />
                      <span className="truncate">{employee.email}</span>
                    </div>
                    {employee.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" strokeWidth={1.5} />
                        <span>{employee.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className={employee.is_active ? 'text-emerald-600' : 'text-red-600'}>
                        {employee.is_active 
                          ? (language === 'ar' ? 'نشط' : 'Active')
                          : (language === 'ar' ? 'معطل' : 'Inactive')
                        }
                      </span>
                    </div>
                  </div>
                  
                  {canManageEmployees && employee.id !== currentUser?.id && (
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(employee)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(employee.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <UserCog className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">{t('noData')}</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmployeesPage;
